import React, { Suspense, useMemo } from 'react'
import { graphql } from 'babel-plugin-relay/macro'
import { useSuspenseFragment } from 'react-relay-offline'

import { ProductImage } from '../../../../core/components'
import type { ProductSkuImage_Product$key } from './__generated__/ProductSkuImage_Product.graphql'
import { ProductSkuImageLoading } from './ProductSkuImageLoading'
import { useProductPageUrlParams } from '../../hooks'
import { useSelectedSkuLowResolutionImage } from './hooks'

const productFragment = graphql`
  fragment ProductSkuImage_Product on Product {
    skus {
      edges {
        node {
          id
          name
          defaultImage {
            url(size: 1000)
          }
        }
      }
    }
  }
`

type ProductSkuImageContentProps = {
  productRef: ProductSkuImage_Product$key
}

const ProductSkuImageContent = ({
  productRef,
}: ProductSkuImageContentProps) => {
  const product = useSuspenseFragment(productFragment, productRef)
  const { selectedSkuId } = useProductPageUrlParams()
  const selectedSkuLowResolutionImage = useSelectedSkuLowResolutionImage()

  const selectedSku = useMemo(
    () => product.skus.edges.find((sku) => sku.node.id === selectedSkuId),
    [product.skus.edges, selectedSkuId]
  )

  if (!selectedSku) {
    return null
  }

  return (
    <ProductImage
      src={selectedSku.node.defaultImage.url}
      alt={selectedSku.node.name}
      lowResolutionSrc={selectedSkuLowResolutionImage}
    />
  )
}

export type ProductSkuImageProps = {
  productRef: ProductSkuImage_Product$key
}

export const ProductSkuImage = (props: ProductSkuImageProps) => (
  <Suspense fallback={<ProductSkuImageLoading />}>
    <ProductSkuImageContent {...props} />
  </Suspense>
)
